import React from 'react';
import { Box, Typography } from '@mui/material';

const SECTION_TITLES = [
  'Failed hard assertion(s):',
  'Failed soft assertion(s):',
  'Soft assertion warning(s):',
  'How to fix:',
  'Why this failed:',
  'Response snippet:',
] as const;

type ParsedSection = { title: string; body: string };

export type ParsedAgentExecutionResult = {
  kind: 'test_failed' | 'test_passed' | 'request_failed';
  summary: string;
  details: string[];
  sections: ParsedSection[];
};

type ParsedDetailLine = {
  label: string;
  value: string;
  mono?: boolean;
  valueColor?: 'error' | 'success' | 'default';
};

function splitIntoSections(text: string): { preamble: string; sections: ParsedSection[] } {
  const lines = text.split('\n');
  const sections: ParsedSection[] = [];
  const preambleLines: string[] = [];
  let current: { title: string; lines: string[] } | null = null;

  const closeCurrent = () => {
    if (current) {
      sections.push({ title: current.title, body: current.lines.join('\n').trim() });
      current = null;
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trimEnd();
    const inlineWhy = trimmedLine.match(/^Why this failed:\s*(.+)$/);
    if (inlineWhy) {
      closeCurrent();
      sections.push({ title: 'Why this failed:', body: inlineWhy[1].trim() });
      continue;
    }

    const matchedTitle = SECTION_TITLES.find((title) => trimmedLine === title);
    if (matchedTitle) {
      closeCurrent();
      current = { title: matchedTitle, lines: [] };
      continue;
    }
    if (current) {
      current.lines.push(line);
    } else {
      preambleLines.push(line);
    }
  }
  closeCurrent();

  return { preamble: preambleLines.join('\n').trim(), sections };
}

function bodyToLines(body: string): string[] {
  return body
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (line.startsWith('- ') ? line.slice(2) : line));
}

const HTTP_METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']);

export function parseDetailLine(line: string, kind?: ParsedAgentExecutionResult['kind']): ParsedDetailLine {
  const colonIdx = line.indexOf(': ');
  if (colonIdx > 0) {
    const label = line.slice(0, colonIdx).trim();
    const value = line.slice(colonIdx + 2).trim();
    const labelLower = label.toLowerCase();
    const mono = labelLower === 'url';
    const valueColor =
      labelLower === 'status' && (/HTTP\s+[45]\d{2}/i.test(value) || /^[45]\d{2}\b/.test(value))
        ? 'error'
        : labelLower === 'status' && (/HTTP\s+2\d{2}/i.test(value) || /^2\d{2}\b/.test(value))
          ? 'success'
          : labelLower === 'error'
            ? 'error'
            : 'default';
    return { label, value, mono, valueColor };
  }

  // POST:/api/auth/login (no space after colon)
  const methodColonMatch = line.match(/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS):(\/\S*)$/i);
  if (methodColonMatch) {
    return {
      label: methodColonMatch[1].toUpperCase(),
      value: methodColonMatch[2].trim(),
      mono: true,
      valueColor: 'default',
    };
  }

  const methodMatch = line.match(/^(\w+)\s+(.+?)(?:\s+\((\w+)\))?$/);
  if (methodMatch) {
    const method = methodMatch[1].toUpperCase();
    if (HTTP_METHODS.has(method)) {
      const path = methodMatch[2].trim();
      return { label: method, value: path, mono: true, valueColor: 'default' };
    }
  }

  if (kind === 'request_failed') {
    return { label: 'Request', value: line, valueColor: 'default' };
  }

  return { label: 'Details', value: line, valueColor: 'default' };
}

export function parseAgentExecutionResult(text: string): ParsedAgentExecutionResult | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  let kind: ParsedAgentExecutionResult['kind'] | null = null;
  if (normalized.startsWith('test failed.')) kind = 'test_failed';
  else if (normalized.startsWith('test passed.')) kind = 'test_passed';
  else if (normalized.startsWith('request executed with failure.')) kind = 'request_failed';
  if (!kind) return null;

  const { preamble, sections } = splitIntoSections(trimmed);
  const preambleLines = preamble.split('\n').map((line) => line.trim()).filter(Boolean);
  const summary = preambleLines[0] ?? trimmed;
  const details = preambleLines.slice(1);

  return { kind, summary, details, sections };
}

const labelSx = {
  fontWeight: 700,
  color: 'text.primary',
  mr: 0.5,
};

const valueSx = (mono?: boolean) => ({
  color: 'text.secondary',
  lineHeight: 1.45,
  fontFamily: mono ? 'monospace' : 'inherit',
  fontSize: mono ? '0.78rem' : '0.875rem',
  wordBreak: 'break-word' as const,
  overflowWrap: 'anywhere' as const,
});

const DetailLineRow: React.FC<{ line: ParsedDetailLine }> = ({ line }) => (
  <Typography variant="body2" component="div" sx={{ lineHeight: 1.45 }}>
    <Box component="span" sx={labelSx}>
      {line.label}:
    </Box>
    <Box
      component="span"
      sx={{
        ...valueSx(line.mono),
        color:
          line.valueColor === 'error'
            ? 'error.main'
            : line.valueColor === 'success'
              ? 'success.main'
              : 'text.secondary',
      }}
    >
      {line.value}
    </Box>
  </Typography>
);

const SectionLabel: React.FC<{ title: string }> = ({ title }) => (
  <Typography variant="body2" component="div" sx={{ ...labelSx, display: 'block', mb: 0.35 }}>
    {title.endsWith(':') ? title : `${title}:`}
  </Typography>
);

export type ParsedAssertionLine = {
  label: string;
  expected: string;
  actual: string;
};

export function parseAssertionLine(line: string): ParsedAssertionLine | null {
  const match = line.match(/^(.+?):\s*expected\s+(.+?),\s*got\s+(.+)$/i);
  if (!match) return null;
  return {
    label: match[1].trim(),
    expected: match[2].trim(),
    actual: match[3].trim(),
  };
}

const monoValueSx = {
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  color: 'text.secondary',
  wordBreak: 'break-word' as const,
  overflowWrap: 'anywhere' as const,
};

const AssertionLineRow: React.FC<{ line: string }> = ({ line }) => {
  const parsed = parseAssertionLine(line);
  if (!parsed) {
    return (
      <Typography variant="body2" sx={valueSx(true)}>
        {line}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
      <Box component="span" sx={labelSx}>
        {parsed.label}:
      </Box>{' '}
      <Box component="span" sx={labelSx}>
        expected
      </Box>{' '}
      <Box component="span" sx={monoValueSx}>
        {parsed.expected}
      </Box>
      {', '}
      <Box component="span" sx={labelSx}>
        got
      </Box>{' '}
      <Box component="span" sx={monoValueSx}>
        {parsed.actual}
      </Box>
    </Typography>
  );
};

const AssertionList: React.FC<{ lines: string[]; mono?: boolean }> = ({ lines }) => (
  <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.1 }}>
    {lines.map((line, idx) => (
      <Box component="li" key={`${line}-${idx}`} sx={{ mb: 0.4 }}>
        <AssertionLineRow line={line} />
      </Box>
    ))}
  </Box>
);

type AgentExecutionResultMessageProps = {
  text: string;
  failure?: boolean;
  success?: boolean;
};

const AgentExecutionResultMessage: React.FC<AgentExecutionResultMessageProps> = ({
  text,
  failure,
  success,
}) => {
  const parsed = parseAgentExecutionResult(text);
  if (!parsed) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: 'text.primary' }}>
        {text}
      </Typography>
    );
  }

  const summaryColor = failure ? 'error.main' : success ? 'success.main' : 'text.primary';
  const summaryText = parsed.summary.endsWith('.')
    ? `${parsed.summary.slice(0, -1)}:`
    : parsed.summary.endsWith(':')
      ? parsed.summary
      : `${parsed.summary}:`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
      <Typography variant="body2" fontWeight={700} color={summaryColor} sx={{ lineHeight: 1.45 }}>
        {summaryText}
      </Typography>

      {parsed.details.length > 0 && (
        <Box
          sx={{
            px: 1.1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'rgba(0,0,0,0.04)',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.45,
          }}
        >
          {parsed.details.map((line) => (
            <DetailLineRow key={line} line={parseDetailLine(line, parsed.kind)} />
          ))}
        </Box>
      )}

      {parsed.sections.map((section) => {
        const title = section.title.replace(/:$/, '');
        const isHowToFix = section.title === 'How to fix:';
        const isWhyFailed = section.title === 'Why this failed:';
        const isResponseSnippet = section.title === 'Response snippet:';
        const lines = bodyToLines(section.body);

        return (
          <Box key={section.title}>
            <SectionLabel title={title} />
            {isHowToFix || isWhyFailed ? (
              <Typography variant="body2" sx={{ ...valueSx(), color: 'text.secondary' }}>
                {section.body}
              </Typography>
            ) : isResponseSnippet ? (
              <Box
                component="pre"
                sx={{
                  mt: 0.25,
                  mb: 0,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'rgba(0,0,0,0.06)',
                  color: 'text.secondary',
                  overflowX: 'auto',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {section.body}
              </Box>
            ) : (
              <AssertionList lines={lines} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default AgentExecutionResultMessage;
